import { LitElement, html, css, styleMap, utils } from '@hh/global.js'
const { throttle, } = utils

class HHStickyHeader extends LitElement {
  static properties = {
    closeWhenHidden: {
      type: String,
      attribute: 'close-when-hidden',
    },
    _state: {
      type: Number,
      state: true,
    },
    _height: {
      type: Number,
      state: true,
    },
    _initialHeight: {
      type: Number,
      state: true,
    },
    enableTransparent: {
      type: Boolean,
      attribute: 'enable-transparent',
    },
    transparent: {
      type: Boolean,
      reflect: true,
    }
  }

  constructor() {
    super()

    this.headerBounds = {}
    this.currentScrollTop = 0
    this.closeWhenHidden = 'hh-details-modal'

    this._state = -1

    this.mediaQuery = window.matchMedia('(max-width: 990px)')
    this.isMobileOrTablet = this.mediaQuery.matches
  }

  connectedCallback() {
    super.connectedCallback()
    document.body?.classList?.add('has-sticky-header')
    this.createObserver()

    this._onScroll = throttle(() => this.handleScroll(), 75)
    window.addEventListener('scroll', this._onScroll)

    // Unthrottled on purpose: throttle() here only leads on the first event
    // in a burst and never trails, so the final scroll event landing exactly
    // at scrollTop 0 (e.g. once the page stops scrolling back to the top)
    // can be swallowed inside the throttle window and never re-fire -
    // leaving ".reduced" stuck on (and, for the same reason, transparent
    // mode stuck off - so toggleTransparent() lives here too rather than in
    // the throttled handleScroll()). Both are cheap enough to run on every
    // scroll event directly.
    this._onScrollReducedState = () => {
      this.updateReducedState(window.pageYOffset || document.documentElement.scrollTop)
      if (this.enableTransparent) {
        this.toggleTransparent()
      }
    }
    window.addEventListener('scroll', this._onScrollReducedState)

    this._onMediaChange = (e) => {
      this.isMobileOrTablet = e.matches
    }

    this.mediaQuery.addEventListener('change', this._onMediaChange)

    // Set the correct initial state in case the page loads already
    // scrolled (e.g. browser scroll restoration on back/forward).
    this.updateReducedState(window.pageYOffset || document.documentElement.scrollTop)

    setTimeout(() => {
      this.setSpacerHeight()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('scroll', this._onScroll)
    window.removeEventListener('scroll', this._onScrollReducedState)
    document.body?.classList?.remove('has-sticky-header')
    this.mediaQuery.removeEventListener('change', this._onMediaChange)
  }

  firstUpdated() {
    // Measure synchronously first, so _initialHeight is actually set before
    // toggleTransparent() (below) reads it - it previously only happened
    // inside a setTimeout, a macrotask that runs after toggleTransparent()'s
    // own microtask-only await, so _initialHeight was still undefined at
    // that comparison on the very first render. "scrollTop <= undefined" is
    // always false, so transparent mode silently lost on init depending on
    // timing - this is what made it unstable.
    this.setSpacerHeight()

    // Re-measure shortly after in case late-loading content (webfonts,
    // images) shifts the header/announcement bar's height.
    setTimeout(() => {
      this.setSpacerHeight()
    })

    if (this.enableTransparent) {
      this.toggleTransparent()
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('_state')) {
      document.body.classList[this._state !== 1 ? 'add' : 'remove'](
        'sticky-header-showing'
      )
    }
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this)
  }

  setSpacerHeight() {
    const { height: _heightAnnouncementbar } = document
      .querySelector('.js-announcement-bar')
      ?.getBoundingClientRect() || {height: 0}
    const { height: _heightHeader } = document
      .querySelector('.js-header')
      .getBoundingClientRect()

    this._initialHeight = _heightAnnouncementbar + _heightHeader
    this.height = this.enableTransparent ? 0 : this._initialHeight
    this._state = 0
    HH.setCSSVariable('--hh-spacer-height', `${this.height}px`)

    HH.setCSSVariable('--hh-header-height', `${_heightHeader}px`)
    HH.setCSSVariable('--hh-announcement-bar-height', `${_heightAnnouncementbar}px`)
    HH.setCSSVariable('--hh-total-header-height', `${this._initialHeight}px`)
  }

  async toggleTransparent() {
    await this.updateComplete
    if (this.isAnyModalOpen() || this.isMegaMenuOpen()) {
      this.transparent = false
      return
    }

    // Transparent only at the very top of the page - not "while still
    // within the header's own height", which let it stay on well into the
    // start of a scroll.
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    this.transparent = scrollTop <= 0
  }

  isAnyModalOpen() {
    return utils.all('hh-details-modal', document).some((element) => {
      return (
        element.hasAttribute('toggle-transparent-header') &&
        element.hasAttribute('is-open')
      )
    })
  }

  isMegaMenuOpen() {
    const megaMenu = document.querySelector('hh-mega-menu-drawer')
    return megaMenu && megaMenu.open
  }

  hide() {
    if (this._state === 1) {
      return
    }
    this._state = 1

    requestAnimationFrame(() => {
      HH.setCSSVariable('--hh-header-height', '0px')
      HH.setCSSVariable('--hh-announcement-bar-height', '0px')
      HH.setCSSVariable('--hh-total-header-height', '0px')
    })

    this.header?.querySelectorAll(this.closeWhenHidden)?.forEach((element) => {
      element.setAttribute('open', false)
    })
  }

  reveal() {
    if (this._state === 2) {
      return
    }

    this.setSpacerHeight()
    this._state = 2
  }

  reset() {
    if (this._state === 0) {
      return
    }

    this.setSpacerHeight()
    this._state = 0
  }

  onMouseEnter() {
    if (!this.enableTransparent) return

    this.transparent = false
  }

  onMouseLeave() {
    if (!this.enableTransparent || this.isMobileOrTablet) return
    this.toggleTransparent()
  }

  onMobileMenuOpened(e) {
    if (!this.enableTransparent) return

    const element = e.detail.value
    if (element.hasAttribute('toggle-transparent-header')) {
      this.transparent = false
    }
  }

  onMobileMenuClosed(e) {
    if (!this.enableTransparent) return

    const element = e.detail.value
    if (element.hasAttribute('toggle-transparent-header')) {
      this.toggleTransparent()
    }
  }

  externalRevealHeader() {
    if (this._state === 0) return
    this._state = 0
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    // Always sticky, on mobile/tablet too - no more hide-on-scroll-down/
    // reveal-on-scroll-up. hide()/reveal() are left in place below for
    // anything else that still calls them directly (e.g. closeFromDetails
    // paths), but scroll itself no longer triggers them.
    if (this._state !== 0) {
      this.reset()
    }

    this.currentScrollTop = scrollTop <= 0 ? 0 : scrollTop
  }

  // Compact/"reduced" header state (see the ".reduced" CSS rules in
  // section-header.css): as soon as the page scrolls away from the very
  // top, fold the utility row into the nav row. Not a merchant setting -
  // this is purely a scroll-position UI state.
  updateReducedState(scrollTop) {
    // While a drawer/modal is open, "body.no-scroll" (old code - see
    // -base.min.css) sets position:fixed on the body to lock scrolling,
    // which makes window.pageYOffset read back as 0 even though the page
    // is still scrolled - without this guard, that false "0" flips the
    // header back to its expanded state under an open mega menu/drawer
    // that's still positioned for the compact one. Leave the current state
    // alone until the lock is released and a real scroll position is
    // reported again.
    if (document.body.classList.contains('no-scroll')) return

    const header = document.querySelector('.js-header')
    if (!header) return

    const shouldBeReduced = scrollTop > 0
    if (header.classList.contains('reduced') === shouldBeReduced) return

    header.classList.toggle('reduced', shouldBeReduced)
    this.setSpacerHeight()
  }

  render() {
    return html`<div
        class="container"
        style="${styleMap({
      position: this._state === -1 ? 'relative' : 'fixed',
      transform: this._state === 1 ? `translateY(-100%)` : 'translateY(0)',
    })}"
        @mouseenter=${this.onMouseEnter}
        @mouseleave=${this.onMouseLeave}
        @modalOpened=${this.onMobileMenuOpened}
        @modalClosed=${this.onMobileMenuClosed}
      >
        <slot></slot>
      </div>
      <div
        class="spacer"
      ></div>`
  }

  static styles = css`
    .container {
      left: 0;
      width: 100%;
      z-index: var(--hh-z-index, 100);
      transform: translateY(0);
      transition: transform var(--hh-duration-long, 500ms);
      background-color: var(--hh-background, transparent);
    }

    .spacer {
      position: relative;
      height: var(--hh-spacer-height);
    }
  `
}

customElements.define('hh-sticky-header', HHStickyHeader)